import React, { useCallback, useState } from "react"
import { connect } from "react-redux"
import { useUser } from "@store/profile";

export const showRegisterLightbox = (id, args) => {
  if (lightbox.isOpen()) return false

  let url = `/pages/register_lightbox/${id || 829870}/closeable`
  if (args) {
    url +=
      "?" +
      Object.keys(args)
        .map(k => k + "=" + encodeURIComponent(args[k]))
        .join("&")
  }

  lightbox.init()
  lightbox.fetch(url, true)
  var fn = function() {
    lightbox.lightboxContent.css("overflow", "visible")
  }
  lightbox.lightboxContent.on("newContent", fn)
  lightbox.lightbox.on("lightboxClosed", function() {
    lightbox.lightboxContent.unbind("newContent", fn)
  })
}

/**
 * Hook that can intercept a method and call it only if the user is logged in, otherwise showing
 * a register lightbox
 * @param {function} callback function to call when user is logged in
 * @param {function} handleIntercepted optional callback, when user was not logged in
 * @param {number} lightboxId optional ID for the register lightbox to open if user is not logged in
 * @return {[() => void), boolean]}
 */
export const useAnonymousInterceptor = (
  callback = fn => (typeof fn === "function" ? fn() : true),
  handleIntercepted = null,
  lightboxId = null,
  lightboxArgs = null
) => {
  const { loggedIn } = useUser()
  const [intercepted, setIntercepted] = useState(false)

  const handler = useCallback(
    (...args) => {
      if (loggedIn) {
        return callback(...args)
      } else {
        showRegisterLightbox(lightboxId, lightboxArgs)
        setIntercepted(true)
        if (handleIntercepted) {
          handleIntercepted()
        }
        return false
      }
    },
    [callback, handleIntercepted, lightboxId, lightboxArgs]
  )

  return [handler, intercepted]
}

/**
 * wraps a component by overriding props specified in the propNames param array, interecepting
 * so that if the user is not logged in, will be prompted with a register lightbox dialog
 * @type {(propNames: string[], registerLightboxId?:number) => (Component:any) => JSX.Element}
 */
export default (
  propNames,
  registerLightboxId,
  registerLightboxArgs
) => Component =>
  connect(state => ({ isLoggedIn: state.profile.user.loggedIn }))(props => {
    const newProps = { ...props }
    if (!props.isLoggedIn) {
      propNames
        .filter(p => newProps.hasOwnProperty(p))
        .forEach(p => {
          newProps[p] = () => {
            showRegisterLightbox(
              props.registerLightboxId || registerLightboxId,
              registerLightboxArgs
            )
            return false
          }
        })
    }

    return <Component {...newProps} />
  })
